<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import { formatAed } from '@shared/utils'
import type { OrderFeeLine } from '@modules/orders'

// "Total Applicable Fee" panel (Figma 32590:61477): the charged fee lines
// from /orders/calculate-fees, split into fees that carry 5% VAT and the rest.

interface Props {
  fees: OrderFeeLine[]
  totalFeeAmount: number
  /** Panel heading — the summary row that opened it. */
  title?: string
}

const props = withDefaults(defineProps<Props>(), { title: 'Total Applicable Fee' })

// Half a fil — anything closer than this is the same amount after rounding.
const ROUNDING_TOLERANCE = 0.005

// The BE sends `vatAmount` beside `amount`. When the lines already add up to
// the quoted total, `amount` is VAT-inclusive; otherwise add the VAT so the
// "including 5% VAT" heading is true either way.
const vatIncludedInAmount = computed(() => {
  const sum = props.fees.reduce((total, fee) => total + fee.amount, 0)
  return Math.abs(sum - props.totalFeeAmount) < ROUNDING_TOLERANCE
})

const toRow = (fee: OrderFeeLine) => ({
  key: fee.code,
  label: fee.rate ? `${fee.label} (${fee.rate})` : fee.label,
  amount: vatIncludedInAmount.value ? fee.amount : fee.amount + fee.vatAmount,
})

const PERCENT_BASE = 100
const MAX_RATE_DECIMALS = 2

// The VAT rate a line was charged at, worked back from its own figures
// ("5%") — the app names the rate only when every VAT line shares it.
const vatRateOf = (fee: OrderFeeLine): string | null => {
  const net = vatIncludedInAmount.value ? fee.amount - fee.vatAmount : fee.amount
  if (net <= 0 || fee.vatAmount <= 0) return null
  return `${parseFloat(((fee.vatAmount / net) * PERCENT_BASE).toFixed(MAX_RATE_DECIMALS))}%`
}

// Section headings are the app's (offerings/constants/feeSections.ts).
const vatSectionTitle = (fees: OrderFeeLine[]): string => {
  const rates = new Set(fees.map(vatRateOf))
  const [only] = [...rates]
  return rates.size === 1 && only
    ? `Fees including ${only} VAT on each`
    : 'Fees including VAT on each'
}

const sections = computed(() => {
  const withVat = props.fees.filter((fee) => fee.vatAmount > 0)
  const withoutVat = props.fees.filter((fee) => fee.vatAmount <= 0)
  return [
    { key: 'vat', title: vatSectionTitle(withVat), rows: withVat.map(toRow) },
    // "Remaining Fees" only follows a VAT block; alone it is just "Fees".
    {
      key: 'other',
      title: withVat.length ? 'Remaining Fees' : 'Fees',
      rows: withoutVat.map(toRow),
    },
  ].filter((section) => section.rows.length > 0)
})
</script>

<template>
  <Box direction="col" gap="3" class="fee-breakdown">
    <Box align="center" justify="between" gap="3">
      <Text type="title-lg" weight="semibold" is="h3">{{ title }}</Text>
      <Text type="heading" weight="semibold" is="span"
        ><CurrencyText :text="formatAed(totalFeeAmount)"
      /></Text>
    </Box>
    <Box class="fee-breakdown__rule" />

    <Box
      v-for="section in sections"
      :key="section.key"
      direction="col"
      gap="11px"
      class="fee-breakdown__section"
    >
      <Text type="body-sm" weight="semibold" is="h4">{{ section.title }}</Text>
      <Box is="ul" direction="col" gap="2" class="fee-breakdown__list">
        <Box
          v-for="(row, index) in section.rows"
          :key="row.key"
          is="li"
          align="center"
          justify="between"
          gap="3"
          class="fee-breakdown__row"
          :class="{ 'fee-breakdown__row--last': index === section.rows.length - 1 }"
        >
          <Text type="caption" color="var(--p-surface-700)" is="span">{{ row.label }}</Text>
          <Text type="body" weight="semibold" align="right" is="span" style="white-space: nowrap">
            <CurrencyText :text="formatAed(row.amount)" />
          </Text>
        </Box>
      </Box>
    </Box>

    <Text v-if="!sections.length" type="body-sm" color="muted">No fees apply to this order.</Text>
  </Box>
</template>

<style scoped>
.fee-breakdown__rule {
  height: 1px;
  background: var(--p-surface-100);
}

.fee-breakdown__section {
  padding: 12px;
  border-bottom: 1px solid var(--p-surface-200);
  border-radius: 8px;
  background: var(--p-surface-50);
}

.fee-breakdown__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.fee-breakdown__row {
  padding-bottom: 6px;
  border-bottom: 1px solid var(--p-surface-200);
}

.fee-breakdown__row--last {
  border-bottom: none;
}
</style>
